import { useMemo } from 'react';
import { useAppState } from '@/ui/AppState';
import { navigate } from '@/ui/router';
import { CategoryChip, EmptyState } from '@/ui/components/bits';
import { categoryStats, worstTopics, type Trend } from '@/domain/weakspots';

const TREND_LABEL: Record<Trend, { icon: string; text: string; color: string }> = {
  up: { icon: '↑', text: 'improving', color: 'var(--correct)' },
  down: { icon: '↓', text: 'slipping', color: 'var(--incorrect)' },
  flat: { icon: '→', text: 'steady', color: 'var(--text-muted)' },
  unknown: { icon: '·', text: 'not enough data', color: 'var(--text-faint)' },
};

export function WeakSpotsScreen() {
  const { attempts, rollups, levels } = useAppState();

  const stats = useMemo(() => categoryStats(attempts, rollups), [attempts, rollups]);
  const worst = useMemo(() => worstTopics(stats), [stats]);

  if (stats.length === 0) {
    return (
      <div className="stack">
        <h1>Weak spots</h1>
        <EmptyState icon="◔" title="No answers recorded yet">
          Once you have answered a few cards and questions, this shows your accuracy by topic and
          what to work on.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="stack">
      <h1>Weak spots</h1>

      {worst.length > 0 ? (
        <>
          <h2 className="section-title">Work on these</h2>
          <ul className="list">
            {worst.map((stat) => (
              <li key={stat.category} className="card stack">
                <div className="spread">
                  <CategoryChip category={stat.category} />
                  <span className="mono">
                    {stat.accuracy === null ? '—' : `${Math.round(stat.accuracy * 100)}%`}
                  </span>
                </div>
                <div className="faint">
                  {stat.correct} of {stat.attempts} right
                  {stat.lowConfidence ? ' · too few attempts to be sure' : ''}
                </div>
                <div className="row">
                  <button
                    className="btn btn--sm btn--primary"
                    onClick={() =>
                      navigate('/study', {
                        mode: 'practice',
                        category: stat.category,
                        t: String(Date.now()),
                      })
                    }
                  >
                    Practice this
                  </button>
                  <button
                    className="btn btn--sm"
                    onClick={() =>
                      navigate('/quiz', {
                        mode: 'practice',
                        category: stat.category,
                        t: String(Date.now()),
                      })
                    }
                  >
                    Quiz this
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <h2 className="section-title">Every topic</h2>
      <ul className="list">
        {stats.map((stat) => {
          const trend = TREND_LABEL[stat.trend];
          const level = levels.get(stat.category)?.level ?? 1;
          const pct = stat.accuracy === null ? 0 : Math.round(stat.accuracy * 100);
          return (
            <li key={stat.category} className="card stack">
              <div className="spread">
                <CategoryChip category={stat.category} />
                <span className="mono">{stat.accuracy === null ? '—' : `${pct}%`}</span>
              </div>
              <div className="progress">
                <div
                  className="progress__fill"
                  style={{
                    width: `${pct}%`,
                    background: pct >= 80 ? 'var(--correct)' : pct >= 50 ? 'var(--accent)' : 'var(--incorrect)',
                  }}
                />
              </div>
              <div className="row faint" style={{ justifyContent: 'space-between' }}>
                <span>
                  {stat.correct}/{stat.attempts} · level {level}
                  {stat.lowConfidence ? ' · low confidence' : ''}
                </span>
                <span style={{ color: trend.color }}>
                  {trend.icon} {trend.text}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
